/**
 * Spotify Import Utilities
 *
 * Utilities for importing playlists and tracks from clipboard URLs/URIs
 */

import { QueryClient } from '@tanstack/react-query';
import { playlistService } from '../api/services/playlists';
import { playlistKeys } from '../api/hooks';
import type { PlaylistDetail } from '../api/types';

/**
 * Parse Spotify playlist ID from clipboard text
 *
 * Supports multiple formats:
 * - URL: https://open.spotify.com/playlist/abc123
 * - URL with params: https://open.spotify.com/playlist/abc123?si=xyz
 * - URI: spotify:playlist:abc123
 * - Raw ID: abc123 (22 alphanumeric characters)
 *
 * @param text - Text from clipboard
 * @returns Playlist ID or null if not found
 */
export function parsePlaylistFromClipboard(text: string): string | null {
  const trimmed = text.trim();

  // URI format: spotify:playlist:abc123
  const uriMatch = trimmed.match(/spotify:playlist:([a-zA-Z0-9]+)/);
  if (uriMatch && uriMatch[1]) {
    return uriMatch[1];
  }

  // URL format: https://open.spotify.com/playlist/abc123
  const urlMatch = trimmed.match(/open\.spotify\.com\/playlist\/([a-zA-Z0-9]+)/);
  if (urlMatch && urlMatch[1]) {
    return urlMatch[1];
  }

  // Raw ID format (22 alphanumeric characters)
  const idMatch = trimmed.match(/^([a-zA-Z0-9]{22})$/);
  if (idMatch && idMatch[1]) {
    return idMatch[1];
  }

  return null;
}

/**
 * Parse Spotify track ID from clipboard text
 *
 * Supports multiple formats:
 * - URL: https://open.spotify.com/track/abc123
 * - URL with params: https://open.spotify.com/track/abc123?si=xyz
 * - URI: spotify:track:abc123
 * - Raw ID: abc123 (22 alphanumeric characters)
 *
 * @param text - Text from clipboard
 * @returns Track ID or null if not found
 */
export function parseTrackFromClipboard(text: string): string | null {
  const trimmed = text.trim();

  // URI format: spotify:track:abc123
  const uriMatch = trimmed.match(/spotify:track:([a-zA-Z0-9]+)/);
  if (uriMatch && uriMatch[1]) {
    return uriMatch[1];
  }

  // URL format: https://open.spotify.com/track/abc123
  const urlMatch = trimmed.match(/open\.spotify\.com\/track\/([a-zA-Z0-9]+)/);
  if (urlMatch && urlMatch[1]) {
    return urlMatch[1];
  }

  // Raw ID format (22 alphanumeric characters)
  const idMatch = trimmed.match(/^([a-zA-Z0-9]{22})$/);
  if (idMatch && idMatch[1]) {
    return idMatch[1];
  }

  return null;
}

export interface ImportPlaylistOptions {
  /** QueryClient for data fetching */
  queryClient: QueryClient;
  /** Function to create a new playlist */
  createPlaylist: (data: { name: string; description: string; public: boolean }) => Promise<{ id: string; name: string; uri: string }>;
  /** Function to add tracks to a playlist */
  addTracks: (params: { playlistId: string; data: { uris: string[] } }) => Promise<{ snapshotId: string }>;
  /** Callback to set the active playlist in workbench */
  setActivePlaylist: (playlist: PlaylistDetail) => void;
  /** Whether there are unsaved changes that need confirmation */
  isDirty?: boolean;
}

/**
 * Import a Spotify playlist from clipboard URL/URI
 *
 * Flow:
 * 1. Check for unsaved changes (with user confirmation if isDirty)
 * 2. Read clipboard text
 * 3. Parse playlist ID
 * 4. Fetch source playlist from Spotify
 * 5. Create new playlist copy
 * 6. Copy all tracks to new playlist
 * 7. Load into workbench
 *
 * @param options - Import configuration
 * @returns Promise that resolves when import is complete
 * @throws Error if import fails
 */
export async function importPlaylistFromClipboard(options: ImportPlaylistOptions): Promise<void> {
  const { queryClient, createPlaylist, addTracks, setActivePlaylist, isDirty = false } = options;

  // 1. Check for unsaved changes
  if (isDirty) {
    const confirmed = window.confirm(
      'You have unsaved changes. Creating a new playlist will discard them.\n\nContinue?'
    );
    if (!confirmed) {
      return; // User cancelled
    }
  }

  // 2. Read clipboard
  const clipboardText = await navigator.clipboard.readText();

  // 3. Parse playlist ID from clipboard
  const playlistId = parsePlaylistFromClipboard(clipboardText);

  if (!playlistId) {
    throw new Error('No valid Spotify playlist URL found in clipboard.\n\nExpected format:\nhttps://open.spotify.com/playlist/abc123...');
  }

  // 4. Fetch source playlist from Spotify
  const sourcePlaylist = await queryClient.fetchQuery({
    queryKey: playlistKeys.detail(playlistId),
    queryFn: () => playlistService.get(playlistId),
  });

  if (!sourcePlaylist) {
    throw new Error('Failed to fetch playlist from Spotify. Check the URL.');
  }

  // 5. Create new playlist copy
  const createdPlaylist = await createPlaylist({
    name: `${sourcePlaylist.name} (Copy)`,
    description: sourcePlaylist.description || 'Imported from Spotify',
    public: false,
  });

  // 6. Copy tracks from source to new playlist
  const trackUris = sourcePlaylist.tracks.items.map(pt => pt.track.uri);

  if (trackUris.length > 0) {
    await addTracks({
      playlistId: createdPlaylist.id,
      data: { uris: trackUris },
    });
  }

  // 7. Load into workbench - fetch the complete playlist
  const playlistDetail = await queryClient.fetchQuery({
    queryKey: playlistKeys.detail(createdPlaylist.id),
    queryFn: () => playlistService.get(createdPlaylist.id),
  });

  setActivePlaylist(playlistDetail);
}

export interface ImportTrackOptions {
  /** QueryClient for data fetching */
  queryClient: QueryClient;
  /** Function to add tracks to the current playlist */
  addTracks: (params: { playlistId: string; data: { uris: string[] } }) => Promise<{ snapshotId: string }>;
  /** Current active playlist ID */
  playlistId: string;
}

/**
 * Import a Spotify track from clipboard URL/URI
 *
 * Flow:
 * 1. Read clipboard text
 * 2. Parse track ID
 * 3. Add track to current playlist
 *
 * @param options - Import configuration
 * @returns Promise that resolves when import is complete
 * @throws Error if import fails
 */
export async function importTrackFromClipboard(options: ImportTrackOptions): Promise<void> {
  const { addTracks, playlistId } = options;

  // 1. Read clipboard
  const clipboardText = await navigator.clipboard.readText();

  // 2. Parse track ID from clipboard
  const trackId = parseTrackFromClipboard(clipboardText);

  if (!trackId) {
    throw new Error('No valid Spotify track URL found in clipboard.\n\nExpected format:\nhttps://open.spotify.com/track/abc123...');
  }

  // 3. Add track to playlist
  const trackUri = `spotify:track:${trackId}`;
  await addTracks({
    playlistId,
    data: { uris: [trackUri] },
  });
}
